import { Alert, Card, Space, Typography } from "antd";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LanguageSwitcher } from "../../layout/LanguageSwitcher";

/**
 * The frame every screen that is reachable without an account shares with the
 * login: registration, the three Devise instruction forms and the two token
 * landing pages. They are deliberately outside AppLayout — there is no sidebar
 * to draw, because there is no session to draw it from.
 */
export function PublicPage({
  title,
  children,
  width = 420,
}: {
  title: string;
  children: ReactNode;
  width?: number;
}) {
  const { t } = useTranslation();

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: 24,
        background: "#f5f5f5",
      }}
    >
      <Card style={{ width: "100%", maxWidth: width }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          <LanguageSwitcher />
        </div>

        <div style={{ marginTop: 24 }}>{children}</div>

        <div style={{ marginTop: 24 }}>
          <Link to="/login">{t("auth.access_site")}</Link>
        </div>
      </Card>
    </div>
  );
}

/**
 * What the three "send me instructions" endpoints answer with. They deliberately
 * reply the same way whether or not the address is known, so the message is the
 * whole result — there is nothing else to show.
 */
export interface DeviseStatusResponse {
  status?: { code?: number; message?: string };
  errors?: string[];
}

export function StatusAlert({
  result,
  error,
}: {
  result: DeviseStatusResponse | undefined;
  error: unknown;
}) {
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
        title={deviseError(error)}
      />
    );
  }

  if (result?.status?.message) {
    return (
      <Alert type="success" showIcon style={{ marginBottom: 16 }} title={result.status.message} />
    );
  }

  return null;
}

/**
 * The Devise controllers answer their failures as `errors: [...]` rather than
 * through the Notifications envelope every other endpoint uses, so ApiError's
 * own message is "HTTP 422" — the useful text sits in the body.
 */
export function deviseError(error: unknown): string {
  const body = (error as { body?: DeviseStatusResponse } | null)?.body;
  if (body?.errors?.length) return body.errors.join(", ");
  if (body?.status?.message) return body.status.message;
  return error instanceof Error ? error.message : String(error);
}

/** The links the login screen and the instruction forms offer each other. */
export function PublicLinks({ exclude }: { exclude?: string }) {
  const { t } = useTranslation();

  const links = [
    { to: "/users/password/new", label: t("auth.shared.forgot_password") },
    { to: "/users/confirmation/new", label: t("auth.shared.need_confirmation_instructions") },
    { to: "/users/unlock/new", label: t("auth.shared.need_unlock_instructions") },
    { to: "/welcome/new_user", label: t("auth.shared.need_to_register") },
  ].filter((link) => link.to !== exclude);

  return (
    <Space orientation="vertical" size={4} style={{ marginTop: 16 }}>
      {links.map((link) => (
        <Link key={link.to} to={link.to}>
          {link.label}
        </Link>
      ))}
    </Space>
  );
}
